// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/**
 * The other ways in, under the sign-in and sign-up forms: "Continue with Apple" (on a Mac
 * only), "Continue with Google" (PLAN.md P1-T02) and "Continue with SSO" (P8-T05). Google's and
 * SSO are drawn alike, in the colors of Google's sign-in button guidelines (white on light
 * pages, near-black on dark ones) with Granite's own border, as on its other buttons: Google's
 * darker outline stood out against the form. Apple's follows Apple's
 * guidelines instead: black with a white logo on light pages, white with a black logo on dark
 * ones. All share one size and shape: a 20px mark at the left and the label centered.
 */
import { cn, Icon, resolvedTheme } from "@granite/ui";
import { Link } from "@tanstack/react-router";
import { useState, useSyncExternalStore, type ReactNode } from "react";

import { appleClientId, startAppleSignIn } from "../lib/appleSignIn.js";
import { errorMessage } from "../lib/errors.js";
import { googleClientId, startGoogleSignIn } from "../lib/googleSignIn.js";
import { t } from "../lib/i18n.js";
import { isDesktop, isMac } from "../lib/platform.js";
import { FormAlert } from "./AuthLayout.js";

const base =
  "relative flex h-10 w-full max-w-[400px] items-center justify-center rounded-[4px] border px-11 " +
  "text-[14px] font-medium tracking-[0.25px] [font-family:Roboto,Arial,sans-serif] transition-colors " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60";
const light = "border-border bg-[#fff] text-[#1f1f1f] hover:bg-[#f2f2f2]";
const dark = "border-border bg-[#131314] text-[#e3e3e3] hover:bg-[#202124]";
/** Sign in with Apple: black on light pages, white on dark ones. */
const appleLight = "border-[#000] bg-[#000] text-[#fff] hover:bg-[#1f1f1f]";
const appleDark = "border-[#fff] bg-[#fff] text-[#000] hover:bg-[#f2f2f2]";

/** The theme in effect, following Settings (data-theme) and the OS setting. */
function subscribeTheme(onChange: () => void): () => void {
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  media.addEventListener("change", onChange);
  return () => {
    observer.disconnect();
    media.removeEventListener("change", onChange);
  };
}

function useDark(): boolean {
  return useSyncExternalStore(subscribeTheme, () => resolvedTheme() === "dark");
}

/** Google's "G" mark, in its own colors (allowed on both backgrounds). */
function GoogleMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/** Apple's logo, in the button's text color. */
function AppleMark() {
  return (
    <svg width="17" height="20" viewBox="0 0 814 1000" aria-hidden="true" fill="currentColor">
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
    </svg>
  );
}

function Mark({ children }: { children: ReactNode }) {
  return <span className="absolute inset-y-0 left-3 flex w-5 items-center justify-center">{children}</span>;
}

/** Offered only on a Mac (the owner's call), checked once per page load. */
const onMac = isMac();

export function OtherSignIn({
  next,
  ssoEmail,
  business,
}: {
  /** Where to land after signing in with Apple or Google (a path inside the app). */
  next: string;
  /** Prefills the SSO page with what was typed in the form. */
  ssoEmail?: string;
  /**
   * Signing up for a business account (with a company's Google Workspace account, say): the
   * company's name, which a new account's workspace gets. The Apple and Google buttons wait for
   * a name, and SSO is left out (it is for workspaces that already exist).
   */
  business?: { workspaceName: string };
}) {
  const isDark = useDark();
  const look = isDark ? dark : light;
  const appleLook = isDark ? appleDark : appleLight;
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState<string>();

  if (isDesktop) {
    // A business sign-up needs the company's name on the way, which the browser hand-off
    // does not carry: that starts on the web.
    return business ? null : <DesktopButtons look={look} appleLook={appleLook} ssoEmail={ssoEmail} />;
  }
  const sso = !business;
  const company = business?.workspaceName.trim();
  const apple = Boolean(appleClientId) && onMac;
  if (!googleClientId && !apple && !sso) return null;
  const waitsForName = leaving || (business !== undefined && !company);
  const go = (start: (next: string, workspaceName?: string) => Promise<void>) => {
    setLeaving(true);
    start(next, company).catch((err: unknown) => {
      setLeaving(false);
      setError(errorMessage(err));
    });
  };
  return (
    <div className="mt-6 flex flex-col gap-3" data-testid="other-sign-in">
      <div className="mb-1 flex items-center gap-3 text-xs text-fg-muted" aria-hidden="true">
        <span className="h-px flex-1 bg-border" />
        {t("login.or")}
        <span className="h-px flex-1 bg-border" />
      </div>
      {error && <FormAlert tone="error">{error}</FormAlert>}
      {apple && (
        <div className="flex justify-center">
          <button
            type="button"
            className={cn(base, appleLook)}
            disabled={waitsForName}
            onClick={() => {
              go(startAppleSignIn);
            }}
          >
            <Mark>
              <AppleMark />
            </Mark>
            {t("login.continueApple")}
          </button>
        </div>
      )}
      {googleClientId && (
        <div className="flex justify-center">
          <button
            type="button"
            className={cn(base, look)}
            disabled={waitsForName}
            onClick={() => {
              go(startGoogleSignIn);
            }}
          >
            <Mark>
              <GoogleMark />
            </Mark>
            {t("login.continueGoogle")}
          </button>
        </div>
      )}
      {sso && (
        <div className="flex justify-center">
          <Link to="/sso" search={ssoEmail ? { email: ssoEmail } : {}} className={cn(base, look)}>
            <Mark>
              <Icon name="building" size={20} />
            </Mark>
            {t("login.continueSSO")}
          </Link>
        </div>
      )}
    </div>
  );
}

/**
 * The desktop app (P4-T08): Apple (on macOS), Google and single sign-on finish in the browser,
 * which hands the sign-in back to the app.
 */
function DesktopButtons({ look, appleLook, ssoEmail }: { look: string; appleLook: string; ssoEmail: string | undefined }) {
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState<string>();
  const start = (via: "apple" | "google" | "sso") => {
    setError(undefined);
    void import("../platform/browserSignIn.js")
      .then((m) => m.startBrowserSignIn(via, via === "sso" ? ssoEmail : undefined))
      .then(() => {
        setWaiting(true);
      })
      .catch((err: unknown) => {
        setError(errorMessage(err));
      });
  };
  return (
    <div className="mt-6 flex flex-col gap-3" data-testid="other-sign-in">
      <div className="mb-1 flex items-center gap-3 text-xs text-fg-muted" aria-hidden="true">
        <span className="h-px flex-1 bg-border" />
        {t("login.or")}
        <span className="h-px flex-1 bg-border" />
      </div>
      {error && <FormAlert tone="error">{error}</FormAlert>}
      {waiting && <FormAlert tone="info">{t("login.finishInBrowser")}</FormAlert>}
      {onMac && (
        <div className="flex justify-center">
          <button type="button" className={cn(base, appleLook)} onClick={() => { start("apple"); }}>
            <Mark>
              <AppleMark />
            </Mark>
            {t("login.continueApple")}
          </button>
        </div>
      )}
      <div className="flex justify-center">
        <button type="button" className={cn(base, look)} onClick={() => { start("google"); }}>
          <Mark>
            <GoogleMark />
          </Mark>
          {t("login.continueGoogle")}
        </button>
      </div>
      <div className="flex justify-center">
        <button type="button" className={cn(base, look)} onClick={() => { start("sso"); }}>
          <Mark>
            <Icon name="building" size={20} />
          </Mark>
          {t("login.continueSSO")}
        </button>
      </div>
    </div>
  );
}
